'use client';

import { useState, useRef, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import { useRouter } from 'next/navigation';

interface Notification {
  id: string;
  type: 'escalation' | 'member_invite' | 'approval_request' | 'audit_event' | 'system';
  title: string;
  message: string;
  entityType: string | null;
  entityId: string | null;
  isRead: boolean;
  createdAt: string;
}

const NotificationPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentOrganization, loading: authLoading } = useAuth();
  const router = useRouter();

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated || authLoading || !currentOrganization) return;
    fetchNotifications();
    subscribeToNotifications();
  }, [isHydrated, authLoading, currentOrganization]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const supabase = createClient();

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('organization_id', currentOrganization!.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      setNotifications(
        data?.map((n) => ({
          id: n.id,
          type: n.type,
          title: n.title,
          message: n.message,
          entityType: n.entity_type,
          entityId: n.entity_id,
          isRead: n.is_read,
          createdAt: n.created_at,
        })) || []
      );
    } catch (err: any) {
      console.error('Failed to fetch notifications:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToNotifications = () => {
    const supabase = createClient();

    const channel = supabase
      .channel('notifications-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `organization_id=eq.${currentOrganization!.id}`,
        },
        (payload) => {
          const newNotification = payload.new as any;
          setNotifications((prev) => [
            {
              id: newNotification.id,
              type: newNotification.type,
              title: newNotification.title,
              message: newNotification.message,
              entityType: newNotification.entity_type,
              entityId: newNotification.entity_id,
              isRead: newNotification.is_read,
              createdAt: newNotification.created_at,
            },
            ...prev,
          ]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `organization_id=eq.${currentOrganization!.id}`,
        },
        (payload) => {
          const updatedNotification = payload.new as any;
          setNotifications((prev) =>
            prev.map((n) =>
              n.id === updatedNotification.id
                ? {
                    ...n,
                    isRead: updatedNotification.is_read,
                  }
                : n
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
      );
    } catch (err: any) {
      console.error('Failed to mark notification as read:', err.message);
    }
  };

  const markAllAsRead = async () => {
    try {
      const supabase = createClient();
      const unreadIds = notifications.filter((n) => !n.isRead).map((n) => n.id);

      if (unreadIds.length === 0) return;

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .in('id', unreadIds);

      if (error) throw error;

      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (err: any) {
      console.error('Failed to mark all as read:', err.message);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.type === 'escalation' && notification.entityId) {
      router.push(`/ai-use-case-management`);
      setIsOpen(false);
    } else if (notification.type === 'audit_event') {
      router.push('/audit-log-interface');
      setIsOpen(false);
    }
  };

  const handleQuickAction = async (
    e: React.MouseEvent,
    notification: Notification,
    action: 'accept' | 'decline' | 'approve' | 'reject'
  ) => {
    e.stopPropagation();

    try {
      const supabase = createClient();

      if (notification.type === 'member_invite' && notification.entityId) {
        const status = action === 'accept' ? 'accepted' : 'declined';
        const { error } = await supabase
          .from('organization_invitations')
          .update({ status })
          .eq('id', notification.entityId);

        if (error) throw error;

        await markAsRead(notification.id);
      } else if (notification.type === 'approval_request' && notification.entityId) {
        // Handle approval workflow (implementation depends on your approval system)
        console.log(`${action} approval for ${notification.entityId}`);
        await markAsRead(notification.id);
      }
    } catch (err: any) {
      console.error('Quick action failed:', err.message);
    }
  };

  const getFilteredNotifications = () => {
    if (selectedFilter === 'all') return notifications;
    if (selectedFilter === 'unread') return notifications.filter((n) => !n.isRead);
    return notifications.filter((n) => n.type === selectedFilter);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'escalation':
        return 'ExclamationTriangleIcon';
      case 'member_invite':
        return 'UserPlusIcon';
      case 'approval_request':
        return 'CheckCircleIcon';
      case 'audit_event':
        return 'DocumentTextIcon';
      case 'system':
        return 'InformationCircleIcon';
      default:
        return 'BellIcon';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'escalation':
        return 'text-destructive';
      case 'member_invite':
        return 'text-blue-500';
      case 'approval_request':
        return 'text-yellow-500';
      case 'audit_event':
        return 'text-purple-500';
      case 'system':
        return 'text-muted-foreground';
      default:
        return 'text-foreground';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const filteredNotifications = getFilteredNotifications();

  if (!isHydrated) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative rounded-md p-2 transition-smooth hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <Icon name="BellIcon" size={24} className="text-foreground" />
        {unreadCount > 0 && (
          <span className="absolute right-0 top-0 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-xs font-bold text-destructive-foreground">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 rounded-md bg-popover shadow-elevation-3 transition-smooth">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="font-heading text-base font-semibold text-foreground">
              Notifications
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="font-caption text-xs text-primary transition-smooth hover:text-primary/80"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="flex space-x-1 border-b border-border px-2 py-2">
            {[
              { label: 'All', value: 'all' },
              { label: 'Unread', value: 'unread' },
              { label: 'Escalations', value: 'escalation' },
              { label: 'Invites', value: 'member_invite' },
              { label: 'Approvals', value: 'approval_request' },
              { label: 'Audit', value: 'audit_event' },
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSelectedFilter(filter.value)}
                className={`rounded-md px-3 py-1 font-caption text-xs transition-smooth ${
                  selectedFilter === filter.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <div className="max-h-[500px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Icon name="BellSlashIcon" size={48} className="mb-2 text-muted-foreground" />
                <p className="font-body text-sm text-muted-foreground">
                  No notifications to display
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`cursor-pointer px-4 py-3 transition-smooth hover:bg-muted ${
                      !notification.isRead ? 'bg-muted/50' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <Icon
                        name={getNotificationIcon(notification.type) as any}
                        size={20}
                        className={`mt-0.5 flex-shrink-0 ${getNotificationColor(notification.type)}`}
                      />
                      <div className="flex-1 space-y-1">
                        <div className="flex items-start justify-between">
                          <p className="font-body text-sm font-medium text-foreground">
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <span className="ml-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary"></span>
                          )}
                        </div>
                        <p className="font-caption text-xs text-muted-foreground">
                          {notification.message}
                        </p>
                        <p className="font-caption text-xs text-muted-foreground">
                          {formatTimestamp(notification.createdAt)}
                        </p>

                        {notification.type === 'member_invite' && !notification.isRead && (
                          <div className="mt-2 flex space-x-2">
                            <button
                              onClick={(e) => handleQuickAction(e, notification, 'accept')}
                              className="rounded-md bg-primary px-3 py-1 font-caption text-xs text-primary-foreground transition-smooth hover:bg-primary/90"
                            >
                              Accept
                            </button>
                            <button
                              onClick={(e) => handleQuickAction(e, notification, 'decline')}
                              className="rounded-md border border-border bg-background px-3 py-1 font-caption text-xs text-foreground transition-smooth hover:bg-muted"
                            >
                              Decline
                            </button>
                          </div>
                        )}

                        {notification.type === 'approval_request' && !notification.isRead && (
                          <div className="mt-2 flex space-x-2">
                            <button
                              onClick={(e) => handleQuickAction(e, notification, 'approve')}
                              className="rounded-md bg-primary px-3 py-1 font-caption text-xs text-primary-foreground transition-smooth hover:bg-primary/90"
                            >
                              Approve
                            </button>
                            <button
                              onClick={(e) => handleQuickAction(e, notification, 'reject')}
                              className="rounded-md border border-border bg-background px-3 py-1 font-caption text-xs text-foreground transition-smooth hover:bg-muted"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {filteredNotifications.length > 0 && (
            <div className="border-t border-border p-3 text-center">
              <button
                onClick={() => {
                  router.push('/audit-log-interface');
                  setIsOpen(false);
                }}
                className="font-caption text-xs text-primary transition-smooth hover:text-primary/80"
              >
                View all activity
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;